import { ChangeEvent, useState } from "react";
import MenuFormPage from "../menu-page/menu-form-page";
import RadioButtons, { Option } from "../../common/radio-buttons/radio-buttons";
import { useAuth } from "../../../hooks/useAuth";
import BodyText from "../../common/body-text/body-text";
import FakeButton from "@/components/fake-button";

const BugReportTextField = ({
  label,
  value,
  handleChange,
  maxLength,
  required = false,
}: {
  label: string;
  value: string | undefined;
  handleChange: (e: ChangeEvent<HTMLInputElement>) => void;
  maxLength: number;
  required?: boolean;
}) => {
  return (
    <div>
      <div>{label}</div>
      <input
        required={required}
        className={"w-full h-auto font-comic normal-case"}
        type={"text"}
        value={value}
        onChange={handleChange}
        maxLength={maxLength}
      />
      {/*<textarea className={"w-full h-auto font-comic"} value={value} onChange={handleChange} maxLength={maxLength}/>*/}
    </div>
  );
};

const BugReportBigTextField = ({
  label,
  value,
  handleChange,
  maxLength,
}: {
  label: string;
  value: string | undefined;
  handleChange: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  maxLength: number;
}) => {
  return (
    <div>
      <div>{label}</div>
      <textarea
        className={"w-full h-auto font-comic normal-case"}
        value={value}
        onChange={handleChange}
        maxLength={maxLength}
      />
    </div>
  );
};
const BugReportRadioField = <T extends number | string>({
  label,
  value,
  handleChange,
  values,
}: {
  label: string;
  value: T;
  handleChange: (e: string) => void;
  values: Option<T>[];
}) => {
  return (
    <div>
      <div>{label}</div>
      <RadioButtons
        label={label}
        value={value}
        options={values}
        onSelect={handleChange}
        hideLabel={true}
      />
    </div>
  );
};
const BugReportDropdownField = <T extends number | string>({
  label,
  value,
  handleChange,
  values,
}: {
  label: string;
  value: string | undefined;
  handleChange: (e: ChangeEvent<HTMLSelectElement>) => void;
  values: Option<T>[];
}) => {
  return (
    <div>
      <div>{label}</div>
      <select onChange={handleChange}>
        {values.map((v) => (
          <option key={v.value} value={v.value}>
            {v.label ?? v.value}
          </option>
        ))}
      </select>
    </div>
  );
};

const BugReport = () => {
  const lastRoomCode = localStorage.getItem("roomCode") || "";

  const [date, setDate] = useState("");
  const [summary, setSummary] = useState("");
  const [roomCode, setRoomCode] = useState<string>(lastRoomCode);
  const [errorDisplayed, setErrorDisplayed] = useState<string>("n/a");
  const [frequency, setFrequency] = useState<string>("once");
  const [additionalInfo, setAdditionalInfo] = useState<string | undefined>("");

  const { isAuthenticated, getAccessTokenSilently, user } = useAuth();

  const [submitted, setSubmitted] = useState(false);

  function stringToBool(guy: string) {
    if (guy.toLowerCase() === "yes") {
      return true;
    } else if (guy.toLowerCase() === "no") {
      return false;
    } else {
      return undefined;
    }
  }

  async function handleSubmit() {
    try {
      // Get Auth0 token
      const token = await getAccessTokenSilently();

      if (!token) {
        console.error('No token received from Auth0');
        alert('Authentication error. Please try logging out and back in.');
        return;
      }

      console.log('Token received, length:', token.length);

      // Capture device/browser info automatically
      const deviceInfo = {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language,
        screenResolution: `${window.screen.width}x${window.screen.height}`,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
      };

      // Send bug report email via Cloudflare Worker
      // Use API host to determine environment since import.meta.env.PROD is true for all production builds (including previews)
      const isProductionApi = import.meta.env.VITE_APP_API_HOST?.includes('api2.franticfanfic.com');
      const emailWorkerUrl = import.meta.env.DEV
        ? 'http://localhost:8788' // Local development with wrangler dev
        : isProductionApi
          ? 'https://bug-report-mailer-prod.franticfanfic.workers.dev' // Production deployment
          : 'https://bug-report-mailer-dev.franticfanfic.workers.dev'; // Development/Preview deployment

      console.log('Sending bug report to:', emailWorkerUrl);

      const response = await fetch(emailWorkerUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          userId: user?.user_id || 'unknown',
          summary,
          date,
          frequency,
          deviceInfo,
          roomCode,
          errorDisplayed: stringToBool(errorDisplayed),
          additionalInfo,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Bug report submission failed:', response.status, errorData);
        throw new Error(`Failed to submit bug report: ${response.status}`);
      }

      setSubmitted(true);
    } catch (error) {
      console.error('Failed to send bug report:', error);
      alert('Failed to submit bug report. Please try again or email franticfanficgame@gmail.com directly.');
    }
  }

  return (
    <MenuFormPage
      titles={["report a bug"]}
      subHeaders={[
        "have you experienced a bug? submit it here!",
        "you must be logged in to submit a bug report.",
      ]}
      submit={handleSubmit}
      ready={!submitted && isAuthenticated}
    >
      {!submitted ? (
        <div
          className={
            "m-4 normal-case text-base font-sans space-y-2 flex flex-col"
          }
        >
          <BugReportTextField
            required
            label={"Please describe your issue:"}
            value={summary}
            handleChange={(e) => setSummary(e.target.value)}
            maxLength={200}
          />
          <BugReportTextField
            required
            label={"When did this happen:"}
            value={date}
            handleChange={(e) => setDate(e.target.value)}
            maxLength={100}
          />
          <BugReportTextField
            label={"What is the room code:"}
            value={roomCode}
            handleChange={(e) => setRoomCode(e.target.value)}
            maxLength={6}
          />
          <BugReportRadioField
            label={"Was there an error message:"}
            value={errorDisplayed}
            handleChange={(e) => setErrorDisplayed(e)}
            values={[
              { value: "no", label: "No" },
              { value: "yes", label: "Yes" },
              {
                value: "n/a",
                label: "N/A",
              },
            ]}
          />

          <BugReportDropdownField
            label={"Frequency of issue:"}
            value={frequency}
            handleChange={(e) => setFrequency(e.target.value)}
            values={[
              { value: "once" },
              { value: "hardly ever" },
              { value: "occasionally" },
              { value: "every time" },
            ]}
          />

          <BugReportBigTextField
            label={"Please add any additional info here (error message, etc):"}
            value={additionalInfo}
            handleChange={(e) => setAdditionalInfo(e.target.value)}
            maxLength={500}
          />

          <BodyText>
            If you have a screenshot of the issue, please email it to
            franticfanficgame@gmail.com.
          </BodyText>

          <BodyText>Thank you!</BodyText>
        </div>
      ) : (
        <div className={"flex-1 flex flex-col self-center justify-center px-2"}>
          <FakeButton size="4xl">
            Thank you for submitting a bug report!{" "}
          </FakeButton>
        </div>
      )}
    </MenuFormPage>
  );
};

export default BugReport;
